(() => {
  'use strict';
  if (window.fmdRestockInitialised) return;
  const dialog = document.getElementById('FmdRestockDialog');
  if (!dialog || typeof dialog.showModal !== 'function') return;
  window.fmdRestockInitialised = true;
  const form = dialog.querySelector('form');
  const email = form.querySelector('[name="email"]');
  const fields = form.querySelector('[data-fmd-restock-fields]');
  const status = form.querySelector('[data-fmd-restock-status]');
  const submit = form.querySelector('[type="submit"]');
  const product = dialog.querySelector('[data-fmd-restock-product]');
  const fallback = dialog.querySelector('[data-fmd-restock-fallback]');
  const verification = dialog.querySelector('[data-fmd-restock-verification]');
  const endpoint = '/apps/fmd-restock';
  const id = /^[1-9]\d*$/;
  let opener, selection, generation = 0, busy = false, controller, activeBot;
  let ready = false;

  function feedback(message, error = false) {
    status.textContent = message;
    status.hidden = false;
    status.setAttribute('role', error ? 'alert' : 'status');
    status.focus();
  }
  function reset() {
    generation++;
    controller?.abort();
    controller = null;
    busy = false;
    submit.disabled = false;
    submit.textContent = 'Notify me';
    form.removeAttribute('aria-busy');
    email.value = '';
    status.hidden = true;
    status.textContent = '';
    fields.hidden = false;
    selection = null;
    try { activeBot?.reset(verification); } catch { /* No personal data is logged. */ }
    activeBot = null;
  }
  function upgrade(root) {
    if (!ready) return;
    const candidates = [...root.querySelectorAll('[data-fmd-enquire]')];
    if (root.matches?.('[data-fmd-enquire]')) candidates.push(root);
    for (const trigger of candidates) {
      if (!id.test(trigger.dataset.productId || '') || !id.test(trigger.dataset.variantId || '')) continue;
      // Never infer DC from a product title. An authoritative catalogue check
      // remains mandatory at signup; explicit DC can retain the enquiry route.
      if (trigger.dataset.fmdDiscontinued === 'true') continue;
      trigger.removeAttribute('data-fmd-enquire');
      trigger.setAttribute('data-fmd-restock', '');
      trigger.textContent = 'Notify me';
      trigger.setAttribute('aria-controls', 'FmdRestockDialog');
      trigger.setAttribute('aria-label', `Notify me when back in stock: ${trigger.dataset.productTitle || 'selected item'}${trigger.dataset.variantTitle && trigger.dataset.variantTitle !== 'Default Title' ? ` — ${trigger.dataset.variantTitle}` : ''}`);
    }
  }
  // The production bridge must supply a real verified challenge adapter. No
  // static token, preview token or silent bypass is supplied by the theme.
  async function initialise() {
    const bot = window.fmdRestockBot;
    if (ready || !bot || typeof bot.getToken !== 'function' || typeof bot.reset !== 'function') return;
    try {
      const response = await fetch(`${endpoint}/readiness`, { credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal: AbortSignal.timeout(5000) });
      if (!response.ok || !response.headers.get('content-type')?.includes('application/json')) return;
      const data = await response.json();
      if (data.enabled !== true || data.contract !== 'restock-only-v1') return;
      ready = true;
      upgrade(document);
      new MutationObserver(records => {
        for (const record of records) for (const node of record.addedNodes) if (node.nodeType === 1) upgrade(node);
      }).observe(document.body, { childList: true, subtree: true });
    } catch { /* Keep the working enquiry flow when readiness cannot be proved. */ }
  }
  window.addEventListener('fmd:restock-bot-ready', initialise);
  initialise();
  dialog.querySelector('[data-fmd-restock-close]').addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', event => {
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
  });
  dialog.addEventListener('close', () => { reset(); if (opener?.isConnected) opener.focus(); });
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-fmd-restock]');
    if (!ready || !trigger || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    reset();
    opener = trigger;
    const d = trigger.dataset;
    selection = { productId: d.productId, variantId: d.variantId };
    product.textContent = `${d.productTitle || 'Selected item'}${d.variantTitle && d.variantTitle !== 'Default Title' ? ` — ${d.variantTitle}` : ''}`;
    // Only retain the observed native mailto fallback, never an arbitrary URL.
    fallback.href = trigger.protocol === 'mailto:' ? trigger.href : 'mailto:support@findmydeal.com.au';
    activeBot = window.fmdRestockBot;
    dialog.showModal();
    email.focus();
  });
  form.addEventListener('submit', async event => {
    event.preventDefault();
    if (busy || !form.reportValidity()) return;
    if (!selection || !id.test(selection.productId || '') || !id.test(selection.variantId || '')) {
      feedback('Close this form and select the item you want a restock email for.', true); return;
    }
    const current = generation, item = { ...selection }, address = email.value.trim();
    busy = true;
    submit.disabled = true;
    submit.textContent = 'Submitting request…';
    form.setAttribute('aria-busy', 'true');
    status.hidden = true;
    controller = new AbortController();
    const requestController = controller;
    const signal = requestController.signal;
    const timeout = setTimeout(() => requestController.abort(), 20000);
    try {
      const challenge = activeBot.getToken({ container: verification, signal });
      const botToken = await Promise.race([challenge, new Promise((_, reject) => signal.addEventListener('abort', () => reject(new Error('request_aborted')), { once: true }))]);
      if (current !== generation || signal.aborted) return;
      if (typeof botToken !== 'string' || !botToken || botToken.length > 1500) throw new Error('verification_required');
      const response = await fetch(`${endpoint}/subscribe`, {
        method: 'POST', credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...item, email: address, consent: true, botToken })
      });
      const data = response.headers.get('content-type')?.includes('application/json') ? await response.json() : null;
      if (current !== generation) return;
      if (response.status === 202 && data?.accepted === true) {
        email.value = '';
        fields.hidden = true;
        feedback('Check your inbox to confirm your restock request. If you already requested an email for this item, your existing request is unchanged.');
      } else if (response.status === 409) {
        feedback('We can’t accept a restock request for this item right now. Check its current availability or email Hudson for help.', true);
      } else if (response.status === 429) {
        feedback('Too many requests were made recently. Please try again later or email Hudson for help.', true);
      } else if (response.status === 403) {
        feedback('We couldn’t verify this request. Please try again or email Hudson for help.', true);
      } else {
        feedback('We couldn’t confirm your request. Check your inbox before trying again, or email Hudson for help.', true);
      }
    } catch {
      if (current === generation) feedback('We couldn’t confirm your request. Check your inbox before trying again, or email Hudson for help.', true);
    } finally {
      clearTimeout(timeout);
      if (current === generation) {
        busy = false;
        submit.disabled = false;
        submit.textContent = 'Notify me';
        form.removeAttribute('aria-busy');
        try { activeBot?.reset(verification); } catch { /* No personal data is logged. */ }
      }
    }
  });
})();
